import { useMemo, useState } from 'react';
import type { JSX, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Checkbox } from './Checkbox';
import { EmptyState } from './EmptyState';
import { Spinner } from './Spinner';
import { FOCUS_RING, cx } from './styles';
import type { StatusLevel } from './styles';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface TableColumn<Row> {
  /** 唯一键，同时是排序状态的标识 */
  key: string;
  header: ReactNode;
  /**
   * 单元格内容。不提供时直接显示 `sortValue` 的结果 ——
   * "取值函数写两遍"（一遍给排序、一遍给显示）是列定义里最容易写歪的地方。
   */
  render?: (row: Row, rowIndex: number) => ReactNode;
  /**
   * 排序取值。**返回数字就按数值比**，返回字符串按本地化顺序比，
   * 返回 `null` 表示这一行没有值。类型决定了比较方式，调用方不需要额外声明。
   */
  sortValue?: (row: Row) => number | string | null;
  /** 自定义比较；优先级高于 `sortValue`（例如"按字符串长度"这种非字典序） */
  compare?: (a: Row, b: Row) => number;
  /** 默认由 `sortValue`/`compare` 是否存在推断；显式 `false` 可以关掉 */
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  width?: string;
  /** 表头的 `title`：补单位、补全称 */
  headerTitle?: string;
}

export interface TableEmptyProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  level?: StatusLevel;
}

export interface TableProps<Row> {
  rows: readonly Row[];
  columns: readonly TableColumn<Row>[];
  /**
   * 稳定行键。React key、选中集合、全选都依赖它。
   * **不要用数组下标**：排序一切换，下标就指向了别的行，"选中的行"会跟着乱跳。
   */
  getRowKey: (row: Row) => string;
  /** 受控排序：给了 `sort` 或 `onSortChange` 就由外部持有排序状态 */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** 非受控（小静态表）时的初始排序 */
  defaultSort?: SortState | null;
  /**
   * 排序点击循环。默认 `asc-desc-none`：第三次点击回到"原始顺序" ——
   * 参数表天然有序（按索引/名称），必须能回到那个顺序，否则用户一旦点了排序就回不去了。
   */
  sortCycle?: 'asc-desc-none' | 'asc-desc';
  selectable?: boolean;
  selectedKeys?: readonly string[];
  onSelectionChange?: (keys: readonly string[]) => void;
  isRowDisabled?: (row: Row) => boolean;
  /** 单行的可访问名称（"选择 高度上限"）。不给就退化成"选择第 N 行" */
  getRowLabel?: (row: Row) => string;
  loading?: boolean;
  empty?: TableEmptyProps;
  /** 完全自定义空状态，优先于 `empty` */
  emptyState?: ReactNode;
  /** 表格说明（视觉隐藏，读屏器会读） */
  caption?: ReactNode;
  stickyHeader?: boolean;
  /** 给了它 Table 自己就是滚动容器（粘性表头也才有意义） */
  maxHeight?: string | number;
  density?: 'compact' | 'normal';
  /** 整行可点。会同时给行加 `tabIndex` 与回车响应，不再只是"鼠标能用" */
  onRowClick?: (row: Row) => void;
  footer?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

const ALIGN: Record<'start' | 'center' | 'end', string> = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right',
};

const DENSITY: Record<'compact' | 'normal', string> = {
  compact: 'px-2 py-1',
  normal: 'px-3 py-1.5',
};

function isSortable<Row>(column: TableColumn<Row>): boolean {
  return column.sortable ?? (column.compare !== undefined || column.sortValue !== undefined);
}

/**
 * 值比较。
 *
 * 数字列必须按**数值**排，不能按字符串排 —— 这是表格里最常见的错误：
 * 字符串序下 `'10' < '9'`，参数表的数值列会排成 1、10、100、2…，
 * 用户看到的是"排序坏了"。这里的类型分支就是为这一条存在的。
 */
export function compareValues(a: number | string | null, b: number | string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') {
    // NaN 视为"没有值"，排在最后：把 NaN 参与比较会让整个排序结果变成未定义行为
    if (Number.isNaN(a)) return 1;
    if (Number.isNaN(b)) return -1;
    return a - b;
  }
  // 同一列里混了数字和字符串时统一按字符串比：否则 '10' 与 9 谁大取决于谁在左边
  if (typeof a === 'number' || typeof b === 'number') {
    return String(a).localeCompare(String(b), 'zh-Hans-CN');
  }
  return a.localeCompare(b, 'zh-Hans-CN');
}

/** 把列定义编译成"列键 → 比较器"的映射。排序时不再逐次查找闭包 */
export function buildComparators<Row>(
  columns: readonly TableColumn<Row>[],
): ReadonlyMap<string, (a: Row, b: Row) => number> {
  const comparators = new Map<string, (a: Row, b: Row) => number>();
  for (const column of columns) {
    if (!isSortable(column)) continue;
    const compare = column.compare;
    const sortValue = column.sortValue;
    comparators.set(column.key, (a, b) => {
      if (compare !== undefined) return compare(a, b);
      if (sortValue === undefined) return 0;
      return compareValues(sortValue(a), sortValue(b));
    });
  }
  return comparators;
}

/** 纯函数排序：需要"在组件外先排好"时（几千行的参数表放在 store 里）可以直接用 */
export function sortRows<Row>(
  rows: readonly Row[],
  comparators: ReadonlyMap<string, (a: Row, b: Row) => number>,
  sort: SortState | null,
): readonly Row[] {
  if (sort === null) return rows;
  const compare = comparators.get(sort.key);
  if (compare === undefined) return rows;
  const factor = sort.direction === 'asc' ? 1 : -1;
  /*
   * 必须复制再排：`Array.prototype.sort` 是**原地**排序，直接排 props 传进来的数组
   * 等于在改调用方的数据（React 里就是改 props）。排序方向一切换就会现原形。
   */
  return [...rows].sort((a, b) => compare(a, b) * factor);
}

/*
 * ============================================================================
 * 上千行的表：怎么避免"每次渲染都重算"
 * ============================================================================
 *
 * 参数列表动辄上千行，而父组件（遥测驱动）可能每秒重渲染很多次。所以：
 *
 * 1. 比较器只编译一次。`buildComparators(columns)` 用 `useMemo` 依赖 `columns` 的
 *    引用，不依赖渲染次数。
 * 2. 排序结果用 `useMemo`，依赖 `rows` / `comparators` / 排序键 / 方向。
 *    关键是排序状态的依赖取**拆开的基本值**（key、direction）而不是 `sort` 对象：
 *    调用方写 `<Table sort={{ key, direction }} />` 这种内联对象时，对象每次渲染
 *    都是新的，用对象做依赖会让 memo 永远失效 —— 这正是"排序每帧都跑一遍"的根因。
 * 3. 选中集合、可见行键、全选状态各自 memo，且只依赖真正相关的东西。
 * 4. Table 不持有任何数据副本，也不做筛选；筛选由调用方在 `rows` 上完成。
 *
 * 调用方需要配合的两件事（做不到的话上面的 memo 会失效，性能问题会回来）：
 *   - `columns` 用模块级常量或 `useMemo`，不要内联字面量数组；
 *   - `getRowKey` / `isRowDisabled` 用模块级函数或 `useCallback`。
 * 这些在开发期不会有任何症状，只会在数千行时表现为"拖动滑块卡"。因此写在文档里。
 *
 * 关于虚拟滚动：本组件**不做**（不引第三方库，见包边界）。判断依据见
 * 交付说明：千行量级下真正的开销在 DOM 节点数（1000 行 × 5 列 ≈ 5000+ 节点）。
 * 需要窗口化时，调用方仍然可以自己切 `rows`（排序、选中都以 `getRowKey` 为键，
 * 切片不会破坏它们），不需要换组件。
 */
export function Table<Row>({
  rows,
  columns,
  getRowKey,
  sort,
  onSortChange,
  defaultSort,
  sortCycle = 'asc-desc-none',
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isRowDisabled,
  getRowLabel,
  loading = false,
  empty,
  emptyState,
  caption,
  stickyHeader = false,
  maxHeight,
  density = 'normal',
  onRowClick,
  footer,
  className,
  'aria-label': ariaLabel,
}: TableProps<Row>): JSX.Element {
  const controlled = sort !== undefined || onSortChange !== undefined;
  const [innerSort, setInnerSort] = useState<SortState | null>(defaultSort ?? null);
  const sortState = controlled ? (sort ?? null) : innerSort;

  const sortKey = sortState === null ? null : sortState.key;
  const sortDirection = sortState === null ? null : sortState.direction;

  const comparators = useMemo(() => buildComparators(columns), [columns]);
  const sortedRows = useMemo(
    () =>
      sortRows(
        rows,
        comparators,
        sortKey === null || sortDirection === null ? null : { key: sortKey, direction: sortDirection },
      ),
    [rows, comparators, sortKey, sortDirection],
  );

  const selectedSet = useMemo(() => new Set(selectedKeys ?? []), [selectedKeys]);
  const visibleKeys = useMemo(
    () => sortedRows.filter((row) => isRowDisabled?.(row) !== true).map(getRowKey),
    [sortedRows, isRowDisabled, getRowKey],
  );

  const selectedVisibleCount = visibleKeys.filter((key) => selectedSet.has(key)).length;
  const allSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
  const someSelected = selectedVisibleCount > 0 && !allSelected;

  function changeSort(next: SortState | null): void {
    if (controlled) onSortChange?.(next);
    else setInnerSort(next);
  }

  function toggleSort(column: TableColumn<Row>): void {
    if (!isSortable(column) || loading) return;
    const key = column.key;
    if (sortState === null || sortState.key !== key) {
      changeSort({ key, direction: 'asc' });
      return;
    }
    if (sortState.direction === 'asc') {
      changeSort({ key, direction: 'desc' });
      return;
    }
    // 已是 desc：第三次点击回到"没有排序"（原始顺序）
    changeSort(sortCycle === 'asc-desc' ? { key, direction: 'asc' } : null);
  }

  /** 输出键的顺序按当前可见行给出，被筛掉的键保留在末尾，不让"全选可见行"顺手清掉别处的选择 */
  function orderKeys(keys: ReadonlySet<string>): readonly string[] {
    const ordered: string[] = [];
    for (const row of sortedRows) {
      const key = getRowKey(row);
      if (keys.has(key)) ordered.push(key);
    }
    for (const key of keys) {
      if (!ordered.includes(key)) ordered.push(key);
    }
    return ordered;
  }

  function toggleRow(key: string, next: boolean): void {
    if (onSelectionChange === undefined) return;
    const keys = new Set(selectedSet);
    if (next) keys.add(key);
    else keys.delete(key);
    onSelectionChange(orderKeys(keys));
  }

  function toggleAll(next: boolean): void {
    if (onSelectionChange === undefined) return;
    const keys = new Set(selectedSet);
    for (const key of visibleKeys) {
      if (next) keys.add(key);
      else keys.delete(key);
    }
    onSelectionChange(orderKeys(keys));
  }

  const colSpan = columns.length + (selectable ? 1 : 0);
  const cellPadding = DENSITY[density];
  const rowInteractive = onRowClick !== undefined;
  const stickyCell = stickyHeader ? 'sticky top-0 bg-surface-2' : undefined;

  return (
    <div
      className={cx('overflow-auto', className)}
      {...(maxHeight === undefined ? {} : { style: { maxHeight } })}
    >
      {/* 粘性表头不需要 z-index：`position: sticky` 的元素本来就画在后来的静态内容之上 */}
      <table
        className="w-full border-collapse text-sm"
        aria-label={ariaLabel}
        aria-busy={loading ? true : undefined}
      >
        {caption === undefined ? null : <caption className="sr-only">{caption}</caption>}

        <thead>
          <tr>
            {selectable ? (
              <th scope="col" className={cx('w-8 border-b border-line text-left', cellPadding, stickyCell)}>
                <Checkbox
                  value={allSelected ? true : someSelected ? 'unknown' : false}
                  onChange={toggleAll}
                  disabled={loading || visibleKeys.length === 0}
                  aria-label="全选当前可见行"
                />
              </th>
            ) : null}

            {columns.map((column) => {
              const sortable = isSortable(column);
              const active = sortable && sortState !== null && sortState.key === column.key;
              const direction = active && sortState !== null ? sortState.direction : null;
              const align = ALIGN[column.align ?? 'start'];

              return (
                <th
                  key={column.key}
                  scope="col"
                  title={column.headerTitle}
                  /*
                   * `aria-sort` 只标在可排序的列上。它是读屏器判断"这一列是不是排序依据、
                   * 朝哪个方向"的唯一依据 —— 只画一个箭头等于什么都没说。
                   */
                  aria-sort={
                    sortable
                      ? direction === 'asc'
                        ? 'ascending'
                        : direction === 'desc'
                          ? 'descending'
                          : 'none'
                      : undefined
                  }
                  className={cx(
                    'border-b border-line text-xs font-medium whitespace-nowrap text-fg-muted',
                    cellPadding,
                    align,
                    stickyCell,
                  )}
                  {...(column.width === undefined ? {} : { style: { width: column.width } })}
                >
                  {sortable ? (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => toggleSort(column)}
                      className={cx(
                        'inline-flex items-center gap-1 rounded-[2px] hover:text-fg disabled:cursor-not-allowed disabled:opacity-50',
                        align,
                        FOCUS_RING,
                      )}
                    >
                      <span>{column.header}</span>
                      {direction === null ? null : (
                        <span aria-hidden="true" className="text-[9px]">
                          {direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className={cx('border-b border-line text-center', cellPadding)}>
                <Spinner label="正在读取…" />
              </td>
            </tr>
          ) : sortedRows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="border-b border-line">
                {emptyState ?? (
                  <EmptyState
                    compact
                    title={empty?.title ?? '暂无数据'}
                    description={empty?.description}
                    action={empty?.action}
                    icon={empty?.icon}
                    level={empty?.level ?? 'neutral'}
                  />
                )}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, rowIndex) => {
              const key = getRowKey(row);
              const selected = selectedSet.has(key);
              const rowDisabled = isRowDisabled?.(row) === true;

              return (
                <tr
                  key={key}
                  data-selected={selected ? 'true' : undefined}
                  data-disabled={rowDisabled ? 'true' : undefined}
                  /*
                   * 整行可点时同时给键盘一条路（`tabIndex` + 回车）。只加 onMouse 的
                   * 可点行，对键盘用户等于不存在 —— 而"点一下打开详情"正是最需要它的地方。
                   */
                  {...(rowInteractive && !rowDisabled
                    ? {
                        tabIndex: 0,
                        onClick: () => onRowClick(row),
                        onKeyDown: (event: ReactKeyboardEvent) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          onRowClick(row);
                        },
                      }
                    : {})}
                  className={cx(
                    'hover:bg-surface-2 data-[selected=true]:bg-accent-soft',
                    rowInteractive && !rowDisabled && 'cursor-pointer',
                    rowInteractive && FOCUS_RING,
                  )}
                >
                  {selectable ? (
                    <td className={cx('border-b border-line', cellPadding)}>
                      <Checkbox
                        value={selected}
                        onChange={(next) => toggleRow(key, next)}
                        disabled={loading || rowDisabled}
                        aria-label={
                          getRowLabel === undefined ? `选择第 ${rowIndex + 1} 行` : `选择 ${getRowLabel(row)}`
                        }
                      />
                    </td>
                  ) : null}

                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cx(
                        'border-b border-line text-fg',
                        cellPadding,
                        ALIGN[column.align ?? 'start'],
                        column.align === 'end' && 'tabular-nums',
                      )}
                    >
                      {column.render === undefined
                        ? formatCellValue(column.sortValue?.(row))
                        : column.render(row, rowIndex)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>

        {footer === undefined ? null : (
          <tfoot>
            <tr>
              <td colSpan={colSpan} className={cx('border-b border-line text-xs text-fg-muted', cellPadding)}>
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** 没有 `render` 时的默认单元格内容：直接用排序取值函数的返回值 */
function formatCellValue(value: number | string | null | undefined): ReactNode {
  if (value === null || value === undefined) return '';
  return String(value);
}
